import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { CheckCircle2, ChevronRight, Mail, MapPin, Phone, Send } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { enableAddressAutocomplete } from "@/lib/places";

const phoneDisplay = "(305) 605-3931";
const phoneHref = "+13056053931";
const email = "assistant@mypremiertitle.com";
const address = "20900 NE 30th Ave, Unit 503, Aventura, FL 33180";
const storageKey = "premier-title-attribution";

export type Attribution = Record<"utm_source" | "utm_medium" | "utm_campaign" | "utm_term" | "utm_content" | "gclid", string>;

export function captureFirstTouchAttribution() {
  if (typeof window === "undefined" || window.sessionStorage.getItem(storageKey)) return;
  const query = new URLSearchParams(window.location.search);
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"] as const;
  const attribution = keys.reduce((result, key) => ({ ...result, [key]: query.get(key) ?? "" }), {} as Attribution);
  window.sessionStorage.setItem(storageKey, JSON.stringify(attribution));
}

function currentAttribution(): Attribution {
  if (typeof window === "undefined") return { utm_source: "", utm_medium: "", utm_campaign: "", utm_term: "", utm_content: "", gclid: "" };
  captureFirstTouchAttribution();
  try {
    return JSON.parse(window.sessionStorage.getItem(storageKey) ?? "{}") as Attribution;
  } catch {
    return { utm_source: "", utm_medium: "", utm_campaign: "", utm_term: "", utm_content: "", gclid: "" };
  }
}

export function formatPhone(value: string) {
  let digits = value.replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function visiblePhoneDigits(value: string) {
  return value.replace(/\D/g, "").length;
}

function formatCurrency(value: string) {
  const digits = value.replace(/\D/g, "");
  return digits ? Number(digits).toLocaleString("en-US") : "";
}

export const quoteAmountRequirements = {
  "Purchase with financing": { purchase: true, loan: true },
  "Cash purchase": { purchase: true, loan: false },
  Refinance: { purchase: false, loan: true },
  "New construction": { purchase: true, loan: true },
  Commercial: { purchase: true, loan: true },
} as const;

export function getQuoteAmountRequirements(transactionType: string) {
  return quoteAmountRequirements[transactionType as keyof typeof quoteAmountRequirements] ?? { purchase: false, loan: false };
}

function formError(value: string, label: string, required = true) {
  if (required && !value.trim()) return `Please enter ${label.toLowerCase()}`;
  return "";
}

function PageMeta({ title, description, noIndex = false }: { title: string; description?: string; noIndex?: boolean }) {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = meta?.content;
    const robotsMeta = noIndex ? document.createElement("meta") : null;
    document.title = title;
    if (meta && description) meta.content = description;
    if (robotsMeta) {
      robotsMeta.name = "robots";
      robotsMeta.content = "noindex, nofollow";
      robotsMeta.dataset.utilityPageRobots = "true";
      document.head.append(robotsMeta);
    }
    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== undefined) meta.content = previousDescription;
      robotsMeta?.remove();
    };
  }, [title, description, noIndex]);
  return null;
}

function FormHiddenFields({ formType, startedAt }: { formType: "homepage_short" | "title_quote_full" | "contact_form"; startedAt: number }) {
  return <>
    <input type="hidden" name="form_type" value={formType} />
    <input type="hidden" name="page_url" value="" />
    <input type="hidden" name="referrer" value="" />
    <input type="hidden" name="submitted_at" value="" />
    <input type="hidden" name="started_at" value="" />
  </>;
}

function Honeypot({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <div className="honeypot" aria-hidden="true"><label>Leave this field empty<input tabIndex={-1} autoComplete="off" value={value} onChange={event => onChange(event.target.value)} /></label></div>;
}

function RequiredNote() { return <p className="required-note"><span>*</span> Required</p>; }

function FieldError({ text, id }: { text?: string; id?: string }) { return text ? <span id={id} className="form-error" role="alert">{text}</span> : null; }

function SubmitError({ text }: { text?: string }) { return text ? <p className="form-submit-error" role="alert">{text}</p> : null; }

function usePublicForm() {
  const [, setLocation] = useLocation();
  const mutation = trpc.forms.submit.useMutation();
  const [submissionError, setSubmissionError] = useState("");
  const startRef = useRef(Date.now());

  const submit = async (input: Parameters<typeof mutation.mutateAsync>[0], destination: string) => {
    setSubmissionError("");
    try {
      await mutation.mutateAsync(input);
      setLocation(destination);
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    } catch (error) {
      setSubmissionError(error instanceof Error ? error.message : "Unable to send your request. Please call us at (305) 605-3931.");
    }
  };

  return { mutation, submissionError, startRef, submit };
}

function buildCommonPayload(formType: "homepage_short" | "title_quote_full" | "contact_form", name: string, emailValue: string, phone: string, startedAt: number, honeypot: string, fields: Record<string, string>) {
  return {
    formType,
    name,
    email: emailValue,
    phone,
    pageUrl: window.location.href,
    referrer: document.referrer || undefined,
    startedAt,
    submittedAt: Date.now(),
    honeypot,
    fields,
    attribution: currentAttribution(),
  };
}

export function HomepageContactForm() {
  const [name, setName] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [addressParts, setAddressParts] = useState({ city: "", county: "", zip: "" });
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { mutation, submissionError, startRef, submit } = usePublicForm();

  const validate = () => {
    const next = {
      name: formError(name, "your name"),
      email: !emailValue.trim() ? "Please enter your email address" : !/^\S+@\S+\.\S+$/.test(emailValue) ? "Please enter a valid email address" : "",
      phone: visiblePhoneDigits(phone) < 10 ? "Please enter your phone number" : "",
      service: service ? "" : "Please select a service",
      smsConsent: smsConsent ? "" : "Please agree to receive text messages before submitting",
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;
    submit(buildCommonPayload("homepage_short", name, emailValue, phone, startRef.current, honeypot, {
      Service: service,
      "Property address": propertyAddress,
      City: addressParts.city,
      County: addressParts.county,
      ZIP: addressParts.zip,
      "Additional notes": additionalNotes,
      "SMS consent": smsConsent ? "Yes" : "No",
    }), "/thank-you");
  };

  return <section className="section quick-contact-section"><div className="site-container quick-contact-grid"><div className="quick-contact-copy"><span className="small-kicker">Premier Title Solutions</span><h2>Let&apos;s Talk About Your Closing</h2><p>Share a few details and a member of our team will contact you. Need a detailed quote? Use our <Link href="/title-quote">title quote form</Link>.</p><div className="quick-contact-details"><a href="https://maps.app.goo.gl/W2xzMJ9YzwB6qSb7A" target="_blank" rel="noreferrer"><MapPin size={20} />{address}</a><a href={`tel:${phoneHref}`}><Phone size={20} />{phoneDisplay}</a><a href={`mailto:${email}`}><Mail size={20} />{email}</a></div></div><form className="lead-form" onSubmit={handleSubmit} noValidate><RequiredNote /><FormHiddenFields formType="homepage_short" startedAt={startRef.current} /><Honeypot value={honeypot} onChange={setHoneypot} /><label><span>Name *</span><input required autoComplete="name" value={name} onChange={event => setName(event.target.value)} onBlur={() => setErrors(current => ({ ...current, name: formError(name, "your name") }))} /></label><FieldError text={errors.name} /><label><span>Email *</span><input required type="email" autoComplete="email" value={emailValue} onChange={event => setEmailValue(event.target.value)} onBlur={() => setErrors(current => ({ ...current, email: !emailValue.trim() ? "Please enter your email address" : !/^\S+@\S+\.\S+$/.test(emailValue) ? "Please enter a valid email address" : "" }))} /></label><FieldError text={errors.email} /><label><span>Phone *</span><input required type="tel" inputMode="tel" maxLength={16} autoComplete="tel-national" value={phone} onChange={event => setPhone(formatPhone(event.target.value))} onBlur={() => setErrors(current => ({ ...current, phone: visiblePhoneDigits(phone) < 10 ? "Please enter your phone number" : "" }))} /></label><FieldError text={errors.phone} /><label><span>Service *</span><select required value={service} onChange={event => setService(event.target.value)} onBlur={() => setErrors(current => ({ ...current, service: service ? "" : "Please select a service" }))}><option value="">Select a service</option><option>Residential purchase or sale</option><option>Refinance</option><option>Commercial transaction</option><option>Title insurance</option><option>Escrow services</option><option>Other</option></select></label><FieldError text={errors.service} /><label><span>Property address</span><input type="text" value={propertyAddress} placeholder="Street address, or city and ZIP if you don't have one yet." onFocus={event => enableAddressAutocomplete(event.currentTarget, selection => { setPropertyAddress(selection.formattedAddress); setAddressParts({ city: selection.city, county: selection.county, zip: selection.zip }); })} onChange={event => setPropertyAddress(event.target.value)} /></label><small>Street address, or city and ZIP if you don&apos;t have one yet.</small><label><span>Additional notes</span><textarea rows={3} value={additionalNotes} onChange={event => setAdditionalNotes(event.target.value)} /></label><label className="sms-consent"><input type="checkbox" required checked={smsConsent} aria-invalid={Boolean(errors.smsConsent)} onChange={event => { setSmsConsent(event.target.checked); setErrors(current => ({ ...current, smsConsent: event.target.checked ? "" : "Please agree to receive text messages before submitting" })); }} /><span>By checking this box, I agree to receive text messages from Premier Title Solutions at the phone number provided about my request and transaction. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of any purchase or service. See our <Link href="/privacy-policy">Privacy Policy</Link>.</span></label><FieldError text={errors.smsConsent} /><button className="site-button primary" type="submit" disabled={mutation.isPending} onClick={event => handleSubmit(event as unknown as FormEvent<HTMLFormElement>)}>{mutation.isPending ? "Sending..." : <>Get Started <Send size={16} /></>}</button><SubmitError text={submissionError} /></form></div></section>;
}

export function TitleQuotePage() {
  const [name, setName] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [phone, setPhone] = useState("");
  const [identity, setIdentity] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [closingDate, setClosingDate] = useState("");
  const [source, setSource] = useState("");
  const [otherSource, setOtherSource] = useState("");
  const [notes, setNotes] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [addressParts, setAddressParts] = useState({ city: "", county: "", zip: "" });
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const smsConsentRef = useRef<HTMLInputElement>(null);
  const { mutation, submissionError, startRef, submit } = usePublicForm();
  const [today, setToday] = useState("");
  const { purchase: needsPurchaseAmount, loan: needsLoanAmount } = getQuoteAmountRequirements(transactionType);

  useEffect(() => {
    setToday(new Date().toISOString().slice(0, 10));
    const scrollToQuoteForm = () => {
      if (window.location.hash !== "#quote-form") return;
      window.requestAnimationFrame(() => {
        document.getElementById("quote-form")?.scrollIntoView({ block: "start", behavior: "auto" });
      });
    };

    scrollToQuoteForm();
    window.addEventListener("hashchange", scrollToQuoteForm);
    return () => window.removeEventListener("hashchange", scrollToQuoteForm);
  }, []);

  const validate = () => {
    const next = {
      name: formError(name, "your full name"),
      email: !emailValue.trim() ? "Please enter your email address" : !/^\S+@\S+\.\S+$/.test(emailValue) ? "Please enter a valid email address" : "",
      phone: visiblePhoneDigits(phone) < 10 ? "Please enter your phone number" : "",
      identity: identity ? "" : "Please select one",
      transactionType: transactionType ? "" : "Please select one",
      purchaseAmount: needsPurchaseAmount && Number(purchaseAmount.replace(/,/g, "")) <= 0 ? "Please enter a purchase amount greater than 0" : "",
      loanAmount: needsLoanAmount && Number(loanAmount.replace(/,/g, "")) <= 0 ? "Please enter a loan amount greater than 0" : "",
      propertyAddress: formError(propertyAddress, "the property address or city and ZIP"),
      source: source ? "" : "Please select one",
      otherSource: source !== "Other" || otherSource.trim() ? "" : "Please tell us how you heard about us",
      smsConsent: smsConsent ? "" : "Please agree to receive text messages before submitting",
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) {
      if (!smsConsent) window.requestAnimationFrame(() => smsConsentRef.current?.focus());
      return;
    }
    submit(buildCommonPayload("title_quote_full", name, emailValue, phone, startRef.current, honeypot, {
      "I am a...": identity,
      "Transaction type": transactionType,
      ...(needsPurchaseAmount ? { "Purchase amount": purchaseAmount.replace(/,/g, "") } : {}),
      ...(needsLoanAmount ? { "Loan amount": loanAmount.replace(/,/g, "") } : {}),
      "Property address or city + ZIP": propertyAddress,
      City: addressParts.city,
      County: addressParts.county,
      ZIP: addressParts.zip,
      "Estimated closing date": closingDate,
      "How did you hear about us?": source,
      "Please tell us": otherSource,
      "Anything we should know": notes,
      "SMS consent": smsConsent ? "Yes" : "No",
    }), "/thank-you?form=quote");
  };

  const changeTransactionType = (value: string) => {
    setTransactionType(value);
    setErrors(current => ({ ...current, transactionType: value ? "" : "Please select one", purchaseAmount: "", loanAmount: "" }));
  };

  return <>
    <PageMeta title="Florida Title & Closing Cost Quote | Premier Title Solutions" description="Request a title insurance and closing cost quote from Premier Title Solutions for a residential or commercial real estate transaction in Aventura or Florida." />
    <section className="quote-page-hero">
      <div className="site-container">
        <span className="small-kicker">Premier Title Solutions</span>
        <h1>Request a Title &amp; Closing Cost Quote</h1>
        <p>Tell us about your transaction and our team will prepare a quote for your title insurance and closing costs. Florida title insurance premiums are based on the purchase price or loan amount, so please include the amount to help us prepare an accurate quote.</p>
        <a href={`tel:${phoneHref}`} className="site-button quote-call-button">Prefer to talk? Call {phoneDisplay} <Phone size={16} /></a>
      </div>
    </section>
    <section className="section quote-form-section">
      <div className="site-container quote-form-wrap">
        <form id="quote-form" className="quote-form" onSubmit={handleSubmit} noValidate>
          <RequiredNote />
          <FormHiddenFields formType="title_quote_full" startedAt={startRef.current} />
          <Honeypot value={honeypot} onChange={setHoneypot} />
          <div className="quote-fields">
            <div>
              <label><span>Full name *</span><input required autoComplete="name" value={name} onChange={event => setName(event.target.value)} onBlur={() => setErrors(current => ({ ...current, name: formError(name, "your full name") }))} /></label>
              <FieldError text={errors.name} />
            </div>
            <div>
              <label><span>Email *</span><input required type="email" autoComplete="email" value={emailValue} onChange={event => setEmailValue(event.target.value)} onBlur={() => setErrors(current => ({ ...current, email: !emailValue.trim() ? "Please enter your email address" : !/^\S+@\S+\.\S+$/.test(emailValue) ? "Please enter a valid email address" : "" }))} /></label>
              <FieldError text={errors.email} />
            </div>
            <div>
              <label><span>Phone *</span><input required type="tel" inputMode="tel" maxLength={16} autoComplete="tel-national" value={phone} onChange={event => setPhone(formatPhone(event.target.value))} onBlur={() => setErrors(current => ({ ...current, phone: visiblePhoneDigits(phone) < 10 ? "Please enter your phone number" : "" }))} /></label>
              <FieldError text={errors.phone} />
            </div>
            <div>
              <label><span>I am a... *</span><select required value={identity} onChange={event => setIdentity(event.target.value)} onBlur={() => setErrors(current => ({ ...current, identity: identity ? "" : "Please select one" }))}><option value="">Select one</option><option>Buyer</option><option>Seller</option><option>Listing agent</option><option>Buyer&apos;s agent</option><option>Other</option></select></label>
              <FieldError text={errors.identity} />
            </div>
            <div>
              <label><span>Transaction type *</span><select required value={transactionType} onChange={event => changeTransactionType(event.target.value)} onBlur={() => setErrors(current => ({ ...current, transactionType: transactionType ? "" : "Please select one" }))}><option value="">Select one</option><option>Purchase with financing</option><option>Cash purchase</option><option>Refinance</option><option>New construction</option><option>Commercial</option></select></label>
              <FieldError text={errors.transactionType} />
            </div>
            {needsPurchaseAmount && <div>
              <label><span>Purchase amount *</span><span className="money-input"><b>$</b><input required inputMode="numeric" value={purchaseAmount} onChange={event => setPurchaseAmount(formatCurrency(event.target.value))} onBlur={() => setErrors(current => ({ ...current, purchaseAmount: Number(purchaseAmount.replace(/,/g, "")) > 0 ? "" : "Please enter a purchase amount greater than 0" }))} /></span></label>
              <FieldError text={errors.purchaseAmount} />
            </div>}
            {needsLoanAmount && <div>
              <label><span>Loan amount *</span><span className="money-input"><b>$</b><input required inputMode="numeric" value={loanAmount} onChange={event => setLoanAmount(formatCurrency(event.target.value))} onBlur={() => setErrors(current => ({ ...current, loanAmount: Number(loanAmount.replace(/,/g, "")) > 0 ? "" : "Please enter a loan amount greater than 0" }))} /></span></label>
              <FieldError text={errors.loanAmount} />
            </div>}
            <div className="wide-field">
              <label><span>Property address or city + ZIP *</span><input required autoComplete="street-address" value={propertyAddress} onFocus={event => enableAddressAutocomplete(event.currentTarget, selection => { setPropertyAddress(selection.formattedAddress); setAddressParts({ city: selection.city, county: selection.county, zip: selection.zip }); })} onChange={event => setPropertyAddress(event.target.value)} onBlur={() => setErrors(current => ({ ...current, propertyAddress: formError(propertyAddress, "the property address or city and ZIP") }))} /></label>
              <FieldError text={errors.propertyAddress} />
              <small>If you don&apos;t have the full address yet, city and ZIP code is fine.</small>
            </div>
            <div>
              <label><span>Estimated closing date</span><input type="date" min={today} value={closingDate} onChange={event => setClosingDate(event.target.value)} /></label>
            </div>
            <div>
              <label><span>How did you hear about us? *</span><select required value={source} onChange={event => setSource(event.target.value)} onBlur={() => setErrors(current => ({ ...current, source: source ? "" : "Please select one" }))}><option value="">Select one</option><option>Google search</option><option>Google Maps</option><option>Real estate agent referral</option><option>Lender referral</option><option>Instagram</option><option>Past client</option><option>Friend or family</option><option>Other</option></select></label>
              <FieldError text={errors.source} />
            </div>
            {source === "Other" && <div className="full-field">
              <label><span>Please tell us *</span><input required value={otherSource} onChange={event => setOtherSource(event.target.value)} onBlur={() => setErrors(current => ({ ...current, otherSource: otherSource.trim() ? "" : "Please tell us how you heard about us" }))} /></label>
              <FieldError text={errors.otherSource} />
            </div>}
            <div className="full-field">
              <label><span>Anything we should know</span><textarea rows={2} placeholder="For example: condo or HOA property, seller is an LLC or trust, closing remotely, or a tight timeline." value={notes} onChange={event => setNotes(event.target.value)} /></label>
            </div>
          </div>
          <label className="sms-consent"><input ref={smsConsentRef} type="checkbox" required checked={smsConsent} aria-invalid={Boolean(errors.smsConsent)} aria-describedby={errors.smsConsent ? "quote-sms-consent-error" : undefined} onChange={event => { setSmsConsent(event.target.checked); setErrors(current => ({ ...current, smsConsent: event.target.checked ? "" : "Please agree to receive text messages before submitting" })); }} /><span>By checking this box, I agree to receive text messages from Premier Title Solutions at the phone number provided about my request and transaction. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of any purchase or service. See our <Link href="/privacy-policy">Privacy Policy</Link>.</span></label>
          <FieldError id="quote-sms-consent-error" text={errors.smsConsent} />
          <button className="site-button primary" type="submit" disabled={mutation.isPending}>{mutation.isPending ? "Sending..." : <>Request My Quote <Send size={16} /></>}</button>
          <p className="form-privacy">We respect your privacy. Your information is used only to respond to your request.</p>
          <SubmitError text={submissionError} />
        </form>
      </div>
    </section>
  </>;
}

export function ContactRequestForm() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { mutation, submissionError, startRef, submit } = usePublicForm();

  const validate = () => {
    const next = {
      firstName: formError(firstName, "your first name"),
      lastName: formError(lastName, "your last name"),
      email: !emailValue.trim() ? "Please enter your email address" : !/^\S+@\S+\.\S+$/.test(emailValue) ? "Please enter a valid email address" : "",
      message: formError(message, "your message"),
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;
    submit(buildCommonPayload("contact_form", `${firstName.trim()} ${lastName.trim()}`.trim(), emailValue, "", startRef.current, honeypot, {
      "First name": firstName,
      "Last name": lastName,
      Subject: subject,
      Message: message,
    }), "/thank-you?form=contact");
  };

  return <form className="contact-form" onSubmit={handleSubmit} noValidate>
    <h2>Fill The Form Below To Send Us An Email</h2>
    <FormHiddenFields formType="contact_form" startedAt={startRef.current} />
    <Honeypot value={honeypot} onChange={setHoneypot} />
    <div className="field-grid">
      <div><label className="form-field"><span>First Name *</span><input required autoComplete="given-name" value={firstName} onChange={event => setFirstName(event.target.value)} onBlur={() => setErrors(current => ({ ...current, firstName: formError(firstName, "your first name") }))} /></label><FieldError text={errors.firstName} /></div>
      <div><label className="form-field"><span>Last Name *</span><input required autoComplete="family-name" value={lastName} onChange={event => setLastName(event.target.value)} onBlur={() => setErrors(current => ({ ...current, lastName: formError(lastName, "your last name") }))} /></label><FieldError text={errors.lastName} /></div>
      <div><label className="form-field"><span>Email Address *</span><input required type="email" autoComplete="email" value={emailValue} onChange={event => setEmailValue(event.target.value)} onBlur={() => setErrors(current => ({ ...current, email: !emailValue.trim() ? "Please enter your email address" : !/^\S+@\S+\.\S+$/.test(emailValue) ? "Please enter a valid email address" : "" }))} /></label><FieldError text={errors.email} /></div>
      <div><label className="form-field"><span>Subject</span><input value={subject} onChange={event => setSubject(event.target.value)} /></label></div>
      <div className="full-field"><label className="form-field"><span>Type Your Message/Query Here..... *</span><textarea required rows={6} value={message} onChange={event => setMessage(event.target.value)} onBlur={() => setErrors(current => ({ ...current, message: formError(message, "your message") }))} /></label><FieldError text={errors.message} /></div>
    </div>
    <button className="site-button primary" type="submit" disabled={mutation.isPending}>{mutation.isPending ? "Sending..." : <>Send Message <Send size={16} /></>}</button>
    <SubmitError text={submissionError} />
  </form>;
}

export function ThankYouPage({ quote = false }: { quote?: boolean }) {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const isQuote = quote || new URLSearchParams(search).get("form") === "quote";
  const eyebrow = isQuote ? "Quote request received" : "Request received";
  const title = isQuote ? "Your title quote request is in." : "Your request is in good hands.";
  const lead = isQuote
    ? "Thank you for sharing your transaction details. Our team will review the property information, transaction type, and requested timing before preparing a clear title insurance and closing-cost estimate."
    : "Thank you for reaching out to Premier Title Solutions. Your inquiry has been received, and a member of our team will connect with you to understand your closing needs and guide the next step.";
  const steps = isQuote
    ? [
      ["Review", "We review the information you submitted and identify any details needed for an accurate estimate."],
      ["Prepare", "Our team prepares a tailored title insurance and closing-cost quote for your transaction."],
      ["Connect", "A team member will contact you to discuss the quote, timing, and any questions you have."],
    ]
    : [
      ["Review", "We review your request and match it with the appropriate title and escrow support."],
      ["Coordinate", "We reach out to learn about the property, parties, timeline, and transaction details."],
      ["Move Forward", "Once we have the right information, we help you determine the next step toward a seamless closing."],
    ];

  const returnHome = () => {
    setLocation("/");
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  return <><PageMeta title="Thank You From Premier Title Solutions" noIndex /><section className="thank-you-page"><div className="site-container thank-you-grid"><div className="thank-you-content"><div className="thank-you-confirmation"><CheckCircle2 size={28} aria-hidden="true" /><span>{eyebrow}</span></div><h1>{title}</h1><p className="thank-you-lead">{lead}</p><div className="thank-you-actions"><button type="button" className="site-button light" onClick={returnHome}>Return to Home</button>{!isQuote && <Link href="/title-quote" className="site-button outline">Request a Full Quote</Link>}</div><div className="thank-you-urgent"><Phone size={18} aria-hidden="true" /><p><strong>Need help with a time-sensitive closing?</strong><br />Call <a href={`tel:${phoneHref}`}>{phoneDisplay}</a> or email <a href={`mailto:${email}`}>{email}</a>.</p></div></div><aside className="thank-you-panel"><span className="small-kicker">What Happens Next</span><h2>A clear, responsive process from here.</h2><ol className="thank-you-steps">{steps.map(([label, copy], index) => <li key={label}><span>{String(index + 1).padStart(2, "0")}</span><div><h3>{label}</h3><p>{copy}</p></div></li>)}</ol><div className="thank-you-security"><strong>Security reminder</strong><p>For your protection, always confirm wiring instructions by phone. We will not change wiring instructions by email.</p></div></aside></div></section></>;
}
